// TWA (Trusted Web Activity) Detection Utilities

/**
 * Detects if the app is running inside a TWA (Trusted Web Activity)
 * @returns {boolean} True if running in TWA, false otherwise
 */
export function isTWA() {
  // Method 1: Check for android-app:// referrer (most reliable for TWA)
  if (document.referrer && document.referrer.startsWith('android-app://')) {
    console.log('[TWA Detection] Detected via android-app:// referrer');
    return true;
  }

  // Method 2: Check display mode and user agent
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.matchMedia('(display-mode: fullscreen)').matches ||
                       window.matchMedia('(display-mode: minimal-ui)').matches;
  
  const isAndroid = /Android/i.test(navigator.userAgent);
  
  if (isStandalone && isAndroid) {
    console.log('[TWA Detection] Detected via display-mode and Android user agent');
    return true;
  }

  // Method 3: Check for TWA-specific injected interfaces
  if (typeof window.PlayBilling !== 'undefined') {
    console.log('[TWA Detection] Detected via PlayBilling interface');
    return true;
  }
  
  // Method 3b: Digital Goods API existence is NOT reliable for TWA detection
  // The API exists in Chrome/Edge even outside TWA, so we don't check it here
  // The actual service initialization will fail if not in TWA

  // Method 4: Check for specific TWA user agent markers
  // Some TWA implementations add specific markers to the user agent
  if (navigator.userAgent.includes('wv') && isAndroid) {
    console.log('[TWA Detection] Detected via WebView marker in user agent');
    return true;
  }

  return false;
}

/**
 * Checks if Play Billing is available (TWA with billing support)
 * @returns {boolean} True if Play Billing is available
 */
export async function isPlayBillingAvailable() {
  // Digital Goods API presence doesn't guarantee TWA
  // We need to actually try to get the service
  if ('getDigitalGoodsService' in window) {
    try {
      const service = await window.getDigitalGoodsService('https://play.google.com/billing');
      if (service) {
        console.log('[TWA Detection] Digital Goods Service successfully initialized');
        return true;
      }
    } catch (error) {
      console.log('[TWA Detection] Digital Goods API present but service unavailable (not in TWA)');
    }
  }
  
  // Fallback to legacy PlayBilling interface
  if (isTWA() && typeof window.PlayBilling !== 'undefined') {
    console.log('[TWA Detection] Legacy PlayBilling interface detected');
    return true;
  }
  
  return false;
}

/**
 * Gets the payment method based on the current environment
 * @returns {Promise<string>} 'play-billing' for TWA with Play Billing, 'stripe' otherwise
 */
export async function getPaymentMethod() {
  const available = await isPlayBillingAvailable();
  if (available) {
    return 'play-billing';
  }
  return 'stripe';
}

/**
 * Detects if running on Android (regardless of TWA)
 * @returns {boolean} True if Android device
 */
export function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

/**
 * Detects if running on iOS
 * @returns {boolean} True if iOS device
 */
export function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Gets detailed environment information
 * @returns {Promise<Object>} Environment details
 */
export async function getEnvironmentInfo() {
  const playBillingAvailable = await isPlayBillingAvailable();
  const paymentMethod = await getPaymentMethod();
  
  return {
    isTWA: isTWA(),
    isPlayBillingAvailable: playBillingAvailable,
    paymentMethod: paymentMethod,
    isAndroid: isAndroid(),
    isIOS: isIOS(),
    isStandalone: window.matchMedia('(display-mode: standalone)').matches,
    userAgent: navigator.userAgent,
    referrer: document.referrer || 'none'
  };
}

// Log environment info on module load for debugging
getEnvironmentInfo().then(info => {
  console.log('[TWA Detection] Environment info:', info);
});